/**
 * Platform → Workflows: whether Workflows are healthy and keeping up, and whether they leave
 * room for Studio's builds. What needs a look first, then four headline numbers, the runs open
 * now (oldest first), failures in the last day, capacity (the runtime and every autoscaled
 * workload, slots, how long tasks wait, throttling), the integration calls being held, and each
 * organization's runs and spend against its budget. From the ops snapshot's `workflows`, which
 * the API refreshes every 15 seconds from the Workflows database and Prometheus.
 */
import { Link } from 'react-router-dom';

import { StatusPill } from '@/components/runs/StatusPill';
import { AdminSection } from '@/components/shell/AdminSection';
import { StatTile } from '@/components/shell/StatTile';
import type { WorkflowsFindingSeverity, WorkflowsOps } from '@/hooks/useOpsSnapshot';
import { formatUsd } from '@/lib/format';
import { formatAge, formatSpan } from '@/lib/microvm';
import type { StatusTone } from '@/lib/runTelemetry';
import { cn } from '@/lib/utils';

const FINDING_TONES: Record<WorkflowsFindingSeverity, StatusTone> = { critical: 'danger', warning: 'warning', info: 'neutral' };
const FINDING_LABELS: Record<WorkflowsFindingSeverity, string> = { critical: 'Act now', warning: 'Look', info: 'Note' };
/** Share of an organization's budget at which its spend shows as close to the limit. */
const NEAR_BUDGET_SHARE = 0.8;

export function formatWait(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return '—';
  if (seconds >= 60) return `${Math.round(seconds / 60)} min`;
  return seconds < 10 ? `${seconds.toFixed(1)}s` : `${Math.round(seconds)}s`;
}

function share(used: number, total: number): string {
  return total > 0 ? `${Math.round((used / total) * 100)}%` : '—';
}

function orgLink(organizationId: string, name: string) {
  return (
    <Link to={`/organizations/${encodeURIComponent(organizationId)}`} className="truncate text-primary hover:underline">
      {name}
    </Link>
  );
}

function workloadLabel(namespace: string, name: string): string {
  if (name === 'studio-workflow-runtime') return 'Workflows runtime';
  if (namespace === 'studio-temporal') return `Temporal ${name.replace(/^temporal-/, '')}`;
  if (name === 'studio-temporal-worker-publish') return 'Build worker: publishes';
  return name;
}

function Findings({ workflows }: { workflows: WorkflowsOps }) {
  if (workflows.findings.length === 0) {
    return (
      <div className="flex items-center gap-3 rounded-xl border border-border bg-card px-4 py-3 text-sm">
        <StatusPill tone="success">Healthy</StatusPill>
        Runs are finishing, nothing is waiting to start, and builds have room.
      </div>
    );
  }
  return (
    <ul className="divide-y divide-border overflow-hidden rounded-xl border border-border bg-card">
      {workflows.findings.map((finding) => (
        <li key={finding.id} className="flex items-start gap-3 px-4 py-2.5 text-sm">
          <StatusPill tone={FINDING_TONES[finding.severity]} className="shrink-0">
            {FINDING_LABELS[finding.severity]}
          </StatusPill>
          <span>{finding.text}</span>
        </li>
      ))}
    </ul>
  );
}

function Headline({ workflows }: { workflows: WorkflowsOps }) {
  const runs = workflows.runs;
  const system = workflows.system;
  const finished = runs ? runs.lastDay.succeeded + runs.lastDay.failed : 0;
  const runtime = system?.runtime;
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <StatTile
        label="Running now"
        value={runs ? runs.open.running + runs.open.waiting : '—'}
        detail={runs ? `${runs.open.queued} queued · ${runs.open.waiting} waiting on a step` : workflows.runsError}
      />
      <StatTile
        label="Failed in 24h"
        value={runs ? runs.lastDay.failed : '—'}
        detail={runs ? `${runs.lastDay.succeeded} succeeded · ${share(runs.lastDay.failed, finished)} failed` : workflows.runsError}
      />
      <StatTile
        label="Wait to start, p95"
        value={system ? formatWait(system.waitSeconds.workflows ?? 0) : '—'}
        detail={system ? `Builds ${formatWait(system.waitSeconds.builds ?? 0)}` : workflows.systemError}
      />
      <StatTile
        label="Runtime pods"
        value={runtime ? `${runtime.current} of ${runtime.max}` : '—'}
        detail={system ? `${share(system.slots.activity.used, system.slots.activity.total)} of step slots busy` : workflows.systemError}
      />
    </div>
  );
}

type Runs = NonNullable<WorkflowsOps['runs']>;
type System = NonNullable<WorkflowsOps['system']>;

function RunningSection({ runs, now }: { runs: Runs; now: number }) {
  return (
    <AdminSection
      title="Running now"
      description="Open runs across every organization, longest-running first. Steps counts finished steps and loop items."
      bodyClassName="p-0"
    >
      {runs.running.length === 0 ? (
        <p className="px-5 py-4 text-sm text-muted-foreground">No Workflow is running.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-muted-foreground">
              <tr className="border-b border-border">
                <th className="px-3 py-2 font-medium">Workflow</th>
                <th className="px-3 py-2 font-medium">Organization</th>
                <th className="px-3 py-2 font-medium">State</th>
                <th className="px-3 py-2 text-right font-medium">Running for</th>
                <th className="px-3 py-2 text-right font-medium">Steps</th>
                <th className="px-3 py-2 text-right font-medium">Cost</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {runs.running.map((run) => (
                <tr key={run.id}>
                  <td className="max-w-[16rem] px-3 py-2">
                    <span className="block truncate" title={run.id}>
                      {run.workflowName}
                    </span>
                    {run.dryRun || run.retryOf ? (
                      <span className="text-xs text-muted-foreground">{[run.dryRun && 'test run', run.retryOf && 'retry'].filter(Boolean).join(' · ')}</span>
                    ) : null}
                  </td>
                  <td className="max-w-[12rem] px-3 py-2">{orgLink(run.organizationId, run.organizationName)}</td>
                  <td className="px-3 py-2">
                    <StatusPill tone={run.status === 'running' ? 'active' : 'neutral'}>{run.status === 'waiting' ? 'Waiting on a step' : run.status === 'queued' ? 'Queued' : 'Running'}</StatusPill>
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-xs tabular-nums">{formatAge(run.startedAt ?? run.createdAt, now)}</td>
                  <td className="px-3 py-2 text-right font-mono text-xs tabular-nums">{run.stepsDone}</td>
                  <td className="px-3 py-2 text-right font-mono text-xs tabular-nums">{formatUsd(run.costUsd)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminSection>
  );
}

function FailuresSection({ runs, now }: { runs: Runs; now: number }) {
  return (
    <AdminSection title="Failed in the last 24 hours" description="Newest first, with the reason the person who owns the Workflow saw." bodyClassName="p-0">
      {runs.failures.length === 0 ? (
        <p className="px-5 py-4 text-sm text-muted-foreground">No run failed in the last 24 hours.</p>
      ) : (
        <ul className="divide-y divide-border text-sm">
          {runs.failures.map((failure) => (
            <li key={failure.id} className="flex items-start gap-3 px-5 py-2.5">
              <span className="w-12 shrink-0 pt-0.5 text-right font-mono text-[11px] tabular-nums text-muted-foreground">{formatAge(failure.endedAt, now)}</span>
              <div className="min-w-0 flex-1">
                <p className="truncate">
                  <span className="font-medium" title={failure.id}>
                    {failure.workflowName}
                  </span>
                  <span className="text-muted-foreground"> · </span>
                  {orgLink(failure.organizationId, failure.organizationName)}
                  {failure.dryRun ? <span className="text-muted-foreground"> · test run</span> : null}
                </p>
                <p className="text-muted-foreground">{failure.message}</p>
              </div>
              {failure.code ? <span className="shrink-0 font-mono text-[11px] text-muted-foreground">{failure.code}</span> : null}
            </li>
          ))}
        </ul>
      )}
    </AdminSection>
  );
}

function CapacitySection({ system }: { system: System }) {
  const rows: Array<[string, string, string?]> = [
    ['Workflow steps wait to start (p95)', formatWait(system.waitSeconds.workflows ?? 0), `worst in 6h ${formatWait(system.waitSeconds.workflowsWorst6h ?? 0)}`],
    ['Build and publish tasks wait to start (p95)', formatWait(system.waitSeconds.builds ?? 0), `worst in 6h ${formatWait(system.waitSeconds.buildsWorst6h ?? 0)}`],
    ['Step slots busy', `${system.slots.activity.used} of ${system.slots.activity.total}`, share(system.slots.activity.used, system.slots.activity.total)],
    ['Workflow-task slots busy', `${system.slots.workflowTask.used} of ${system.slots.workflowTask.total}`, share(system.slots.workflowTask.used, system.slots.workflowTask.total)],
    ['Steps finished a minute', system.activitiesPerMinute === null ? '—' : String(Math.round(system.activitiesPerMinute))],
    ['Calls Temporal turns away (Workflows / builds)', `${system.throttledPerSecond.workflows.toFixed(1)} / ${system.throttledPerSecond.builds.toFixed(1)} a second`],
  ];
  return (
    <AdminSection
      title="Capacity"
      description="The runtime scales between its minimum and maximum on busy slots and waiting steps. Temporal caps Workflows so builds keep their share, and builds go first for nodes."
    >
      <div className="grid gap-6 lg:grid-cols-2">
        <dl className="divide-y divide-border text-sm">
          {rows.map(([label, value, hint]) => (
            <div key={label} className="flex items-baseline justify-between gap-3 py-1.5">
              <dt className="text-muted-foreground">{label}</dt>
              <dd className="text-right font-mono tabular-nums">
                {value}
                {hint ? <span className="ml-2 text-xs text-muted-foreground">{hint}</span> : null}
              </dd>
            </div>
          ))}
        </dl>
        <div>
          <p className="mb-1.5 text-xs font-medium text-muted-foreground">Pods now, of the autoscaler's range</p>
          {system.scaling.length === 0 ? (
            <p className="text-sm text-muted-foreground">No autoscaler readings yet.</p>
          ) : (
            <ul className="divide-y divide-border text-sm">
              {system.scaling.map((row) => (
                <li key={`${row.namespace}/${row.name}`} className="flex items-baseline justify-between gap-3 py-1.5">
                  <span className="truncate">{workloadLabel(row.namespace, row.name)}</span>
                  <span className={cn('font-mono tabular-nums', row.current >= row.max && 'text-warning')}>
                    {row.current}
                    <span className="text-muted-foreground">
                      {' '}
                      ({row.min}–{row.max})
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      {system.alerts.length > 0 ? (
        <div className="mt-4 flex flex-wrap gap-2">
          {system.alerts.map((alert) => (
            <StatusPill key={`${alert.name}:${alert.namespace ?? ''}`} tone={alert.severity === 'critical' ? 'danger' : 'warning'}>
              {alert.name}
            </StatusPill>
          ))}
        </div>
      ) : null}
    </AdminSection>
  );
}

function CallsSection({ runs, now }: { runs: Runs; now: number }) {
  if (runs.calls.length === 0) return null;
  return (
    <AdminSection
      title="Integration calls held now"
      description="Calls in flight under each organization's call limits, and keys being paced or held off after a Retry-After."
      bodyClassName="p-0"
    >
      <ul className="divide-y divide-border text-sm">
        {runs.calls.map((call) => (
          <li key={`${call.organizationId}:${call.callKey}`} className="flex items-baseline justify-between gap-3 px-5 py-1.5">
            <span className="min-w-0 truncate">
              <span className="font-mono text-xs">{call.callKey}</span>
              <span className="text-muted-foreground"> · </span>
              {orgLink(call.organizationId, call.organizationName)}
            </span>
            <span className="shrink-0 font-mono text-xs tabular-nums">
              {call.inFlight} in flight
              {call.pacedUntil && Date.parse(call.pacedUntil) > now ? (
                <span className="ml-2 text-warning">next in {formatSpan(Date.parse(call.pacedUntil) - now)}</span>
              ) : null}
            </span>
          </li>
        ))}
      </ul>
    </AdminSection>
  );
}

function UsageSection({ workflows }: { workflows: WorkflowsOps & { runs: Runs } }) {
  const { runs, limits } = workflows;
  return (
    <AdminSection
      title="Usage by organization"
      description={`Runs and spend against each organization's monthly budget (default ${formatUsd(limits.orgMonthlyBudgetUsd)}; each Workflow may run ${limits.maxRunsPerDay} times a day unless set otherwise).`}
      bodyClassName="p-0"
    >
      {runs.organizations.length === 0 ? (
        <p className="px-5 py-4 text-sm text-muted-foreground">No organization ran a Workflow this month.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-muted-foreground">
              <tr className="border-b border-border">
                <th className="px-3 py-2 font-medium">Organization</th>
                <th className="px-3 py-2 text-right font-medium">Running</th>
                <th className="px-3 py-2 text-right font-medium">Runs 24h</th>
                <th className="px-3 py-2 text-right font-medium">Failed 24h</th>
                <th className="px-3 py-2 text-right font-medium">Spent this month</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {runs.organizations.map((org) => {
                const budget = org.budgetUsd ?? limits.orgMonthlyBudgetUsd;
                const used = budget > 0 ? org.spentThisMonthUsd / budget : 0;
                return (
                  <tr key={org.organizationId}>
                    <td className="max-w-[16rem] px-3 py-1.5">{orgLink(org.organizationId, org.organizationName)}</td>
                    <td className="px-3 py-1.5 text-right font-mono tabular-nums">{org.runningNow}</td>
                    <td className="px-3 py-1.5 text-right font-mono tabular-nums">{org.runsLastDay}</td>
                    <td className={cn('px-3 py-1.5 text-right font-mono tabular-nums', org.failedLastDay > 0 && 'text-warning')}>{org.failedLastDay}</td>
                    <td className={cn('px-3 py-1.5 text-right font-mono tabular-nums', used >= 1 ? 'text-destructive' : used >= NEAR_BUDGET_SHARE && 'text-warning')}>
                      {formatUsd(org.spentThisMonthUsd)}
                      <span className="text-muted-foreground"> / {formatUsd(budget)}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </AdminSection>
  );
}

function Unavailable({ what, reason }: { what: string; reason?: string }) {
  return (
    <p className="rounded-lg border border-dashed border-border px-4 py-5 text-sm text-muted-foreground">
      {what} {reason ? `are unavailable: ${reason}` : 'are unavailable.'}
    </p>
  );
}

export function WorkflowsTab({ workflows, now }: { workflows: WorkflowsOps | null | undefined; now: number }) {
  if (!workflows) {
    return (
      <p className="rounded-lg border border-dashed border-border px-4 py-5 text-sm text-muted-foreground">
        Workflows are off on this API, or it predates this view.
      </p>
    );
  }
  const runs = workflows.runs;
  return (
    <div className="space-y-6">
      <Findings workflows={workflows} />
      <Headline workflows={workflows} />
      {runs ? (
        <>
          <RunningSection runs={runs} now={now} />
          <FailuresSection runs={runs} now={now} />
        </>
      ) : (
        <Unavailable what="Workflow runs" reason={workflows.runsError} />
      )}
      {workflows.system ? <CapacitySection system={workflows.system} /> : <Unavailable what="Capacity numbers" reason={workflows.systemError} />}
      {runs ? (
        <>
          <CallsSection runs={runs} now={now} />
          <UsageSection workflows={{ ...workflows, runs }} />
        </>
      ) : null}
    </div>
  );
}
